import { useState } from 'react';

/**
 * The Vanam lockup.
 *
 * The artwork itself is used, unaltered: `public/brand/vanam-logo.png`, the file supplied by
 * the owner. Nothing here redraws or recolours it - a wordmark that the application
 * reinterprets is a wordmark that drifts from everything else the company prints.
 *
 * If that file is missing the component falls back to a drawn approximation rather than a
 * broken-image icon, so a fresh clone still renders something sensible. The fallback is a
 * stand-in, not the brand: drop the real file in and it disappears.
 *
 * `variant` only changes the size and the alt text; both render the same artwork.
 *   full    - sign-in and print headers, where there is room for the whole lockup.
 *   compact - the sidebar.
 *   mark    - the check disc alone, for an avatar. Drawn, since the supplied artwork is the
 *             full lockup and cropping it in CSS would be altering it.
 */
const LOGO_SRC = '/brand/vanam-logo.png';

export function VanamLogo({
  variant = 'full',
  height = 40,
  title = 'Vanam',
}: {
  variant?: 'full' | 'compact' | 'mark';
  height?: number;
  title?: string;
}) {
  const [missing, setMissing] = useState(false);

  if (variant === 'mark') {
    return (
      <svg aria-label={title} height={height} role="img" viewBox="0 0 48 48" width={height}>
        <circle cx="24" cy="24" fill="var(--theme-500)" r="22" />
        <path d="M13 24.5 L21 32 L36 16" fill="none" stroke="#fff" strokeLinecap="round"
              strokeLinejoin="round" strokeWidth="6" />
      </svg>
    );
  }

  if (!missing) {
    return (
      <img
        alt={title}
        className={`vanam-logo vanam-logo-${variant}`}
        height={height}
        onError={() => setMissing(true)}
        src={LOGO_SRC}
        style={{ height, width: 'auto' }}
      />
    );
  }

  // Fallback only. See the note above.
  const full = variant === 'full';
  return (
    <svg
      aria-label={title}
      height={height}
      role="img"
      viewBox={full ? '0 0 1040 400' : '0 0 1040 250'}
      width={Math.round(height * (full ? 1040 / 400 : 1040 / 250))}
    >
      <title>{title}</title>
      <text fill="var(--theme-500)" fontFamily="Inter, 'Segoe UI', system-ui, sans-serif"
            fontSize="190" fontWeight="800" letterSpacing="6" x="8" y="170">VANAM.</text>
      <rect fill="var(--brand-rule)" height="7" rx="3" width="800" x="180" y="196" />
      {full && (
        <g>
          <text fill="var(--accent)" fontFamily="Inter, 'Segoe UI', system-ui, sans-serif"
                fontSize="150" fontWeight="700" x="180" y="345">S36</text>
          <circle cx="492" cy="296" fill="var(--theme-500)" r="55" />
          <path d="M466 298 L485 317 L521 276" fill="none" stroke="#fff" strokeLinecap="round"
                strokeLinejoin="round" strokeWidth="15" />
          <text fill="var(--text-tertiary)" fontFamily="Inter, 'Segoe UI', system-ui, sans-serif"
                fontSize="150" fontWeight="600" x="548" y="345">.earth</text>
        </g>
      )}
    </svg>
  );
}
